import axios from 'axios';
import Cookies from 'js-cookie';

// 优先使用全局注入或环境变量，默认回退为相对路径 /api，避免移动端 localhost 跨域问题
const ENV_API_BASE = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_API_BASE_URL) || '';
const API_BASE_URL = (typeof window !== 'undefined' && (window as any).__API_BASE_URL__) || (ENV_API_BASE || '/api');

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 增加为3分钟，避免大图/慢机算超时
});

// 请求拦截器 - 添加认证token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 清除token并跳转到登录页（根据当前路径前缀选择 /matching/login 或 /login）
      Cookies.remove('token');
      try {
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        const loginPath = (path && path.startsWith('/matching')) ? '/matching/login' : '/login';
        window.location.href = loginPath;
      } catch {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// 认证相关API
export const login = async (username: string, password: string) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// 修改密码
export const changePassword = async (oldPassword: string, newPassword: string) => {
  const response = await api.post('/auth/change-password', {
    old_password: oldPassword,
    new_password: newPassword,
  });
  return response.data;
};

// 注册（需邀请码）
export const registerWithInvite = async (
  username: string,
  password: string,
  inviteCode: string,
  securityQuestions?: Array<{ question: string; answer: string }>
) => {
  const response = await api.post('/auth/register', {
    username,
    password,
    invite_code: inviteCode,
    security_questions: securityQuestions && securityQuestions.length > 0 ? securityQuestions : undefined,
  });
  return response.data;
};

// 创建邀请码（仅管理员）
export const createInvite = async (expireHours: number = 24) => {
  const response = await api.post('/auth/invite/create', { expire_hours: expireHours });
  return response.data as { code: string; expires_at: string };
};

// 邀请码列表（仅管理员）
export const listInvites = async () => {
  const response = await api.get('/auth/invite/list');
  return response.data as Array<{ code: string; expires_at: string; active: boolean; used_by?: string; used_at?: string | null }>;
};

// 图片上传相关API
export const uploadImages = async (files: File[], descriptions: string[] = [], tags: string[][] = []) => {
  const formData = new FormData();
  
  files.forEach((file, index) => {
    formData.append('files', file);
    if (descriptions[index]) {
      formData.append('descriptions', descriptions[index]);
    }
    if (tags[index] && tags[index].length > 0) {
      formData.append('tags', tags[index].join(','));
    }
  });

  const response = await api.post('/upload/images', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// 压缩包上传（zip/rar）
export const uploadArchive = async (archiveFile: File, category?: string, engine: string = 'default') => {
  const formData = new FormData();
  formData.append('archive', archiveFile);
  formData.append('file', archiveFile);
  formData.append('engine', engine);
  if (category) formData.append('category', category);
  const response = await api.post('/upload/archive', formData);
  return response.data as { message: string; count: number; results: Array<{ id: number; image_id: number; filename: string; url: string; description?: string; tags?: string[]; category?: string; }> };
};

// 图片管理相关API
export const getImages = async (page: number = 1, pageSize: number = 20, search?: string) => {
  const params: any = { page, page_size: pageSize };
  if (search) params.search = search;
  
  const response = await api.get('/images', { params });
  return response.data;
};

export const getImage = async (imageId: number) => {
  const response = await api.get(`/images/${imageId}`);
  return response.data;
};

export const deleteImage = async (imageId: number) => {
  const response = await api.delete(`/images/${imageId}`);
  return response.data;
};

// 图片搜索相关API
export const searchByImage = async (
  file: File,
  topK: number = 10,
  threshold: number = 0.7,
  engine: string = 'default',
  opts?: { strictBaidu?: boolean; baiduRaw?: boolean; onUploadProgress?: (percent: number) => void }
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('top_k', topK.toString());
  formData.append('threshold', threshold.toString());
  formData.append('engine', engine);
  if (opts?.strictBaidu) formData.append('strict_baidu', '1');
  if (opts?.baiduRaw) formData.append('baidu_raw', '1');

  const response = await api.post('/search/by-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (e) => {
      try {
        if (opts?.onUploadProgress) {
          const percent = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          opts.onUploadProgress(percent);
        }
      } catch {}
    },
  });
  return response.data;
};

export const searchById = async (imageId: number, topK: number = 10, threshold: number = 0.7, engine: string = 'default') => {
  const params = { top_k: topK, threshold, engine };
  const response = await api.get(`/search/by-id/${imageId}`, { params });
  return response.data;
};

export const getSearchHistory = async (limit: number = 10) => {
  const response = await api.get('/search/history', { params: { limit } });
  return response.data;
};

// 图片分析相关API
export const analyzeImage = async (imageId: number) => {
  const response = await api.get(`/images/${imageId}/analyze`);
  return response.data;
};

export const getImageThumbnail = (imageId: number, size: number = 200) => {
  return `${API_BASE_URL}/images/${imageId}/thumbnail?size=${size}`;
};

export const batchAnalyzeImages = async (imageIds: number[]) => {
  const response = await api.post('/images/batch/analyze', { image_ids: imageIds });
  return response.data;
};

// 标签和分类相关API
export const getAllTags = async () => {
  const response = await api.get('/images/tags');
  return response.data;
};

export const getAllCategories = async () => {
  const response = await api.get('/images/categories');
  return response.data;
};

export const updateImageTags = async (imageId: number, tags: string[]) => {
  const response = await api.put(`/images/${imageId}/tags`, { tags });
  return response.data;
};

export const updateImageCategory = async (imageId: number, category: string) => {
  const response = await api.put(`/images/${imageId}/category`, { category });
  return response.data;
};

export const updateImageDescription = async (imageId: number, description: string) => {
  const response = await api.put(`/images/${imageId}/description`, { description });
  return response.data;
};

// 更新图片元信息（后端统一 /images/{id} 接口，使用查询参数）
export const updateImageMeta = async (imageId: number, params: { description?: string; tags?: string[]; category?: string }) => {
  const q: any = {};
  if (typeof params.description === 'string') q.description = params.description;
  if (Array.isArray(params.tags)) q.tags = JSON.stringify(params.tags);
  if (typeof params.category === 'string') q.category = params.category;
  const response = await api.put(`/images/${imageId}`, undefined, { params: q });
  return response.data as { message: string };
};

// 批量操作API
export const batchDeleteImages = async (imageIds: number[]) => {
  const response = await api.post('/images/batch/delete', { image_ids: imageIds });
  return response.data;
};

export const batchUpdateTags = async (imageIds: number[], tags: string[], operation: 'replace' | 'add' | 'remove' = 'replace') => {
  const response = await api.post('/images/batch/tags', { 
    image_ids: imageIds, 
    tags, 
    operation 
  });
  return response.data;
};

export const batchUpdateCategory = async (imageIds: number[], category: string) => {
  const response = await api.post('/images/batch/category', { 
    image_ids: imageIds, 
    category 
  });
  return response.data;
};

// 统计信息API
export const getImageStats = async () => {
  const response = await api.get('/images/stats');
  return response.data;
};

export const getSearchStats = async () => {
  const response = await api.get('/search/stats');
  return response.data;
};

export const getAvailableEngines = async () => {
  const response = await api.get('/search/engines');
  return response.data as { engines: string[] };
};

// 安全问题（注册/找回密码）
export const getSecurityQuestions = async () => {
  const response = await api.get('/auth/security-questions');
  return response.data as { questions: string[] };
};

// 忘记密码：根据用户名获取已设置的安全问题（仅问题，不含答案）
export const forgotPasswordByUsername = async (username: string) => {
  const response = await api.post('/auth/forgot-password-by-username', { username });
  return response.data as { security_questions: string[] };
};

// 忘记密码：回答问题重置密码
export const resetPasswordBySecurity = async (
  username: string,
  answers: string[],
  newPassword: string
) => {
  const response = await api.post('/auth/reset-password-by-security', {
    username,
    answers,
    new_password: newPassword,
  });
  return response.data as { message: string };
};

// 百度任务状态查询
export const getBaiduTaskStatus = async (imageIds: number[]) => {
  const response = await api.post('/images/baidu/tasks/status', imageIds, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data as { statuses: Record<number, any> };
};

export interface ImageSearchResult {
  id: number;
  filename: string;
  category: string;
  tags: string[];
  distance?: number;
  file_path: string;
  description: string;
  baidu_synced: boolean;
  dimensions: string;
  size: number;
}

export default api;
